import { useState, useEffect, useCallback } from "react";
import { Building2, Search, Phone, Mail, MapPin, Star, Package, FileText, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RicambistaOfficine({ user }) {
  const [officine, setOfficine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchOfficine = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch officine collegate al ricambista
      const res = await authFetch(`${API}/ricambista/officine`, { });
      if (res.ok) {
        const data = await res.json();
        setOfficine(data);
      } else {
        // Fallback: prova a caricare dalla lista utenti
        const usersRes = await authFetch(`${API}/users?user_type=OFFICINA`, { });
        if (usersRes.ok) {
          const users = await usersRes.json();
          // Filtra solo officine che hanno interagito con questo ricambista
          setOfficine(users.filter(u => u.linked_ricambista_id === user.user_id || u.ricambista_id === user.user_id));
        }
      }
    } catch (error) {
      console.error("Error fetching officine:", error);
      toast.error("Errore nel caricamento officine");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOfficine();
  }, [fetchOfficine]);

  const filteredOfficine = officine.filter(o => 
    (o.company_name || o.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.city || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.email || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ricambista-officine-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white uppercase font-['Barlow_Condensed'] tracking-wider flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            Archivio Officine
          </h1>
          <p className="text-zinc-400 text-sm">Officine collegate al tuo account</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          type="text"
          placeholder="Cerca per nome, città o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900/50 border-zinc-800"
          data-testid="search-officine"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary" />
              <div>
                <p className="text-zinc-400 text-sm">Totale Officine</p>
                <p className="text-2xl font-bold text-white">{officine.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Officine List */}
      {filteredOfficine.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">
            {searchTerm ? "Nessuna officina trovata" : "Nessuna officina collegata"}
          </p>
          <p className="text-zinc-600 text-sm mt-2">
            Le officine verranno mostrate qui quando invieranno richieste di ricambi
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOfficine.map((officina) => (
            <Card 
              key={officina.user_id || officina.officina_id} 
              className="bg-zinc-900/50 border-zinc-800 hover:border-primary/50 transition-colors"
              data-testid={`officina-card-${officina.user_id || officina.officina_id}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center justify-between">
                  <span className="truncate">{officina.company_name || officina.name}</span>
                  {officina.status === "ATTIVO" && (
                    <Badge className="bg-green-500/20 text-green-400">Attiva</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {officina.email && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${officina.email}`} className="hover:text-primary truncate">
                      {officina.email}
                    </a>
                  </div>
                )}
                {officina.phone && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${officina.phone}`} className="hover:text-primary">
                      {officina.phone}
                    </a>
                  </div>
                )}
                {(officina.city || officina.address) && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">
                      {officina.address && `${officina.address}, `}
                      {officina.city} {officina.province && `(${officina.province})`}
                    </span>
                  </div>
                )}
                
                {/* Stats per questa officina */}
                <div className="flex items-center gap-4 pt-2 mt-2 border-t border-zinc-800">
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <FileText className="w-3 h-3" />
                    <span>{officina.total_requests || 0} richieste</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <Package className="w-3 h-3" />
                    <span>{officina.total_orders || 0} ordini</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
